import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { History, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import {
  addRecentSearch,
  selectRecentSearches,
  selectSearchQuery,
  setQuery,
} from '@features/search/searchSlice';
import { useSearchSuggestions } from '@hooks/useSearchSuggestions';
import { SEARCH } from '@constants/search';
import { cn } from '@/lib/utils';

interface SearchBarProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  showRecentSearches?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  placeholder,
  showRecentSearches = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentQuery = useAppSelector(selectSearchQuery);
  const recentSearches = useAppSelector(selectRecentSearches);

  const [inputValue, setInputValue] = useState(currentQuery);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestions = useSearchSuggestions(inputValue);

  // Follow an external query change (e.g. a recent-search click) without an
  // effect: adjust during render so the input reflects it on the same commit
  // rather than one render later.
  const [prevQuery, setPrevQuery] = useState(currentQuery);
  if (currentQuery !== prevQuery) {
    setPrevQuery(currentQuery);
    setInputValue(currentQuery);
  }

  const showRecent = showRecentSearches && recentSearches.length > 0 && inputValue.length === 0;
  const hasContent = suggestions.length > 0 || showRecent;

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setInputValue(trimmed);
    dispatch(setQuery(trimmed));
    dispatch(addRecentSearch(trimmed));
    setIsOpen(false);
    onSearch(trimmed);
  };

  const handleChange = (value: string) => {
    setInputValue(value);
    dispatch(setQuery(value));
    setIsOpen(true);
  };

  const clear = () => {
    setInputValue('');
    dispatch(setQuery(''));
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <Popover open={isOpen && hasContent} onOpenChange={setIsOpen}>
      <PopoverAnchor asChild>
        <div className="relative w-full">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.9}
          />

          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(event) => handleChange(event.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit(inputValue);
              if (event.key === 'Escape') setIsOpen(false);
            }}
            placeholder={placeholder ?? t('documents.searchPlaceholder')}
            aria-label={placeholder ?? t('documents.searchPlaceholder')}
            className={cn('h-11 pl-9', inputValue && 'pr-10')}
          />

          {inputValue && (
            <button
              type="button"
              onClick={clear}
              aria-label={t('common.clear')}
              className="absolute right-2 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" strokeWidth={2} />
            </button>
          )}
        </div>
      </PopoverAnchor>

      <PopoverContent
        align="start"
        sideOffset={6}
        className="revy-ui max-h-[300px] w-[var(--radix-popover-trigger-width)] overflow-y-auto p-0"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {showRecent && (
          <div className="border-b p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <History className="size-3.5" strokeWidth={1.9} />
              {t('search.recentSearches')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {recentSearches.slice(0, SEARCH.RECENT_SEARCH_LIMIT).map((search) => (
                <button
                  key={search}
                  type="button"
                  onClick={() => submit(search)}
                  className="rounded-full border px-2.5 py-0.5 text-xs transition-colors hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {search}
                </button>
              ))}
            </div>
          </div>
        )}

        {suggestions.length > 0 && (
          <ul className="p-1">
            {suggestions.map((suggestion) => (
              <li key={suggestion}>
                <button
                  type="button"
                  onClick={() => submit(suggestion)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <Search className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.9} />
                  <span className="revy-cjk truncate">{suggestion}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default SearchBar;
